import * as api from 'services/api';
import { addError } from 'actions/error';
import type {
    AssessmentCatalogResponse,
    AssessmentProduct,
    AdminVoucherListItem,
    CreateVoucherLinkResponse,
    VoucherCodeResponse,
    CounselorVoucherReport,
    VoucherListItem,
    VoucherStats,
    VoucherCounts,
    RecommendationProfile,
} from 'types/assessment';

type Dispatch = (action: any) => any;

// Product catalog ----------------------------------------------------------

export const fetchAssessmentCatalog =
    () =>
    (dispatch: Dispatch): Promise<AssessmentCatalogResponse | null> =>
        api
            .get('assessments/products', 'FETCH_ASSESSMENT_CATALOG', dispatch)
            .catch((error: Error) => {
                dispatch(addError(new Error('FETCH_ASSESSMENT_CATALOG_ERROR')));
                return null;
            });

export const fetchAssessmentProduct =
    (slug: string) =>
    (dispatch: Dispatch): Promise<{ data: AssessmentProduct; meta: any } | null> =>
        api
            .get(
                `assessments/products/${encodeURIComponent(slug)}`,
                'FETCH_ASSESSMENT_PRODUCT',
                dispatch,
            )
            .catch((error: Error) => {
                dispatch(addError(new Error('FETCH_ASSESSMENT_PRODUCT_ERROR')));
                return null;
            });

// Counselor inventory ------------------------------------------------------

export const fetchVoucherStats =
    () =>
    (dispatch: Dispatch): Promise<VoucherStats | null> =>
        api
            .get('assessments/vouchers/stats', 'FETCH_VOUCHER_STATS', dispatch)
            .catch((error: Error) => {
                dispatch(addError(new Error('FETCH_VOUCHER_STATS_ERROR')));
                return null;
            });

export const fetchVouchers =
    (params: { status?: string; limit?: number; offset?: number } = {}) =>
    (dispatch: Dispatch): Promise<{ data: VoucherListItem[]; meta: any } | null> => {
        const qs = new URLSearchParams();
        if (params.status) qs.set('status', params.status);
        if (params.limit) qs.set('limit', String(params.limit));
        if (params.offset) qs.set('offset', String(params.offset));
        const query = qs.toString();
        const url = `assessments/vouchers${query ? `?${query}` : ''}`;
        return api
            .get(url, 'FETCH_VOUCHERS', dispatch, undefined, { skipCache: true })
            .then((response: any) => {
                if (!response) return null;

                // Paginated API responses are unwrapped by services/api: the
                // `data` array is returned directly and `meta` is attached to
                // it. Normalize that shared-client contract here so pages do
                // not need to know whether the transport returned an array or
                // the older `{ data, meta }` wrapper.
                const data = Array.isArray(response) ? response : response.data;
                return {
                    data: Array.isArray(data) ? data : [],
                    meta: response.meta || {},
                };
            })
            .catch((error: Error) => {
                dispatch(addError(new Error('FETCH_VOUCHERS_ERROR')));
                return null;
            });
    };

// Assessment administration: read-only inventory across all consultants.
export const fetchAdminVoucherStats =
    () =>
    (dispatch: Dispatch): Promise<{ counts: VoucherCounts } | null> =>
        api.get('assessments/admin/vouchers/stats', 'FETCH_ADMIN_VOUCHER_STATS', dispatch, undefined, { skipCache: true })
            .catch(() => null);

export const fetchAdminVouchers =
    (params: { status?: string; limit?: number; offset?: number } = {}) =>
    (dispatch: Dispatch): Promise<{ data: AdminVoucherListItem[]; meta: { count: number; limit: number; offset: number } } | null> => {
        const qs = new URLSearchParams();
        if (params.status) qs.set('status', params.status);
        if (params.limit) qs.set('limit', String(params.limit));
        if (params.offset) qs.set('offset', String(params.offset));
        const query = qs.toString();
        return api.get(`assessments/admin/vouchers${query ? `?${query}` : ''}`, 'FETCH_ADMIN_VOUCHERS', dispatch, undefined, { skipCache: true })
            .then((response: any) => {
                if (!response) return null;
                const data = Array.isArray(response) ? response : response.data;
                return { data: Array.isArray(data) ? data : [], meta: response.meta || {} };
            })
            .catch(() => null);
    };

export const fetchRecommendationProfiles =
    () =>
    async (dispatch: Dispatch): Promise<RecommendationProfile[] | null> => {
        try {
            const response: any = await api.get(
                'assessments/recommendation-profiles', 'FETCH_RECOMMENDATION_PROFILES',
                dispatch, undefined, { skipCache: true },
            );
            const data = Array.isArray(response) ? response : response?.data;
            return Array.isArray(data) ? data : null;
        } catch (_) {
            return null;
        }
    };

export const setVoucherProfile =
    (voucherId: string, profileId: string | null) =>
    async (dispatch: Dispatch): Promise<{
        voucherId: string;
        profileId: string | null;
        recommendationProfile: RecommendationProfile | null;
    } | null> => {
        try {
            return await api.post(
                `assessments/vouchers/${encodeURIComponent(voucherId)}/profile`,
                { profileId }, 'SET_VOUCHER_PROFILE', dispatch,
            );
        } catch (_) {
            return null;
        }
    };

// POST returns a fresh PDF Blob through the authenticated shared transport.
export const regenerateVoucherPdf =
    (voucherId: string, profileId: string | null) =>
    async (dispatch: Dispatch): Promise<Blob | null> => {
        try {
            const response = await api.post(
                `assessments/vouchers/${encodeURIComponent(voucherId)}/pdf/regenerate`,
                { profileId }, 'REGENERATE_VOUCHER_PDF', dispatch,
            );
            return response instanceof Blob && response.type === 'application/pdf' && response.size > 0
                ? response : null;
        } catch (_) {
            return null;
        }
    };

// Public directory status --------------------------------------------------

/**
 * Check whether the signed-in counselor's linked profile is currently
 * visible through the public consultants API. `null` means the check failed,
 * so callers can fail closed and avoid offering an action that could overwrite
 * an already-published profile.
 */
export const fetchCounselorPublicListingStatus =
    () =>
    async (dispatch: Dispatch): Promise<boolean | null> => {
        try {
            const status: any = await api.get(
                'consultants/me/listing-status',
                'FETCH_COUNSELOR_LISTING_STATUS',
                dispatch,
                undefined,
                { skipCache: true },
            );
            if (typeof status?.isPubliclyListed !== 'boolean') return null;
            return status.isPubliclyListed;
        } catch (_) {
            return null;
        }
    };

export const createVoucherLink =
    (voucherId: string) =>
    (dispatch: Dispatch): Promise<CreateVoucherLinkResponse | null> =>
        api
            .post(
                `assessments/vouchers/${encodeURIComponent(voucherId)}/link`,
                {},
                'CREATE_VOUCHER_LINK',
                dispatch,
            )
            .catch((error: Error) => {
                dispatch(addError(new Error('CREATE_VOUCHER_LINK_ERROR')));
                return null;
            });

export const fetchVoucherCode =
    (voucherId: string) =>
    (dispatch: Dispatch): Promise<VoucherCodeResponse | null> =>
        api
            .get(
                `assessments/vouchers/${encodeURIComponent(voucherId)}/code`,
                'FETCH_VOUCHER_CODE',
                dispatch,
                undefined,
                { skipCache: true },
            )
            .catch((error: Error) => {
                dispatch(addError(new Error('FETCH_VOUCHER_CODE_ERROR')));
                return null;
            });

export const fetchVoucherReport =
    (voucherId: string) =>
    (dispatch: Dispatch): Promise<CounselorVoucherReport> =>
        api.get(
            `assessments/vouchers/${encodeURIComponent(voucherId)}/report`,
            'FETCH_VOUCHER_REPORT',
            dispatch,
            undefined,
            { skipCache: true },
        );

export const setVoucherClient =
    (voucherId: string, body: { name?: string; email?: string }) =>
    (dispatch: Dispatch) =>
        api
            .post(
                `assessments/vouchers/${encodeURIComponent(voucherId)}/client`,
                body,
                'SET_VOUCHER_CLIENT',
                dispatch,
            )
            .catch((error: Error) => {
                dispatch(addError(new Error('SET_VOUCHER_CLIENT_ERROR')));
                return null;
            });

// Buy flow: composes the existing createOrder + startPayment from actions/payment.
// The new path is `type: 'assessmentPack'` with `packId` so pricing comes
// from the catalog. Result of startPayment includes `pageUrl` (Monobank) or
// the equivalent for Stripe/Creem — the caller redirects.

export const buyPack =
    (packId: string) =>
    async (dispatch: Dispatch): Promise<{ pageUrl?: string } | null> => {
        const { createOrder, startPayment } = await import('actions/payment');
        const order: any = await dispatch(
            createOrder({ type: 'assessmentPack', options: { packId } }),
        );
        if (!order || order instanceof Error || !order._id) {
            return null;
        }
        const invoice: any = await dispatch(startPayment(order._id));
        if (!invoice || invoice instanceof Error) return null;
        return invoice;
    };

// Photo upload --------------------------------------------------------------
// Direct Cloudflare Images upload: the server issues a one-shot uploadURL +
// image id; the client POSTs the file to uploadURL as multipart/form-data and
// submits the id on apply.

export interface DirectUploadResponse {
    uploadURL: string;
    id: string;
}

export const getConsultantPhotoUploadUrl =
    () =>
    (dispatch: Dispatch): Promise<DirectUploadResponse | null> =>
        api
            .get(
                'consultants/me/upload-photo-url',
                'GET_CONSULTANT_PHOTO_UPLOAD_URL',
                dispatch,
            )
            .catch((error: Error) => {
                dispatch(addError(new Error('GET_CONSULTANT_PHOTO_UPLOAD_URL_ERROR')));
                return null;
            });

// Apply to be listed --------------------------------------------------------

export const applyForListing =
    (body: Record<string, any>) =>
    (dispatch: Dispatch) =>
        api
            .post('consultants/me/apply', body, 'APPLY_FOR_LISTING', dispatch)
            .catch((error: Error) => {
                dispatch(addError(new Error('APPLY_FOR_LISTING_ERROR')));
                return null;
            });

// Free self-voucher (/pro funnel) ------------------------------------------

export interface SelfVoucherResponse {
    voucherId?: string;
    code?: string;
    redeemUrl?: string;
    status?: string;
    alreadyClaimed?: boolean;
}

export const requestProVoucher =
    () =>
    (dispatch: Dispatch): Promise<SelfVoucherResponse | null> =>
        api
            .post('assessments/vouchers/self', {}, 'REQUEST_PRO_VOUCHER', dispatch)
            .catch((error: Error) => {
                dispatch(addError(new Error('REQUEST_PRO_VOUCHER_ERROR')));
                return null;
            });
